import { billableCalculationLabel, billingNumber, groupBillableCalculationSteps } from "./billingCalculation.js";
import { visitDispatchLabel } from "./consolidationDispatchPresentation.js";

const millKey = (mill) => mill ? String(mill.id ?? JSON.stringify([mill.name, mill.address])) : null;

// Sum source hourly quantities before rounding for display, irrespective of rate.
// Tonne/voyage supplements and physical time excluded from billing are not hours.
export function consolidationHourlyTotal(items) {
  return items.filter((item) => item.billing_unit === "heure").reduce((total, item) => {
    const quantity = billingNumber(item.quantity);
    return total === null || quantity === null || quantity < 0 ? null : total + quantity;
  }, 0);
}

export function formatConsolidationHours(value) {
  const hours = billingNumber(value);
  if (hours === null || hours < 0) return "—";
  const minutes = Math.round(hours * 60);
  if (!minutes && hours > 0) return "< 1 min";
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
}

// The server allocates inspection per source charge, using the invoice calculation.
// Without that presentation, keep the separate inspection instead of guessing a share.
export function presentConsolidationItems(items, presentation) {
  if (presentation?.version !== 1) return items;
  const byKey = new Map(presentation.charges.map((charge) => [charge.key, charge]));
  return items.filter((item) => byKey.has(item.id)).map((item) => {
    const { key, ...display } = byKey.get(item.id);
    return { ...item, ...display };
  });
}

function stopOrders(members, visit, primary, sharedVisit) {
  const kind = primary.calculation_details?.kind;
  const orders = new Map();
  const field = kind === "loading" ? "loaded_tons" : "delivered_tons";
  const add = (id, label, tons) => {
    const key = String(id ?? label);
    if (!orders.has(key)) orders.set(key, { key, label: label || "Commande non renseignée", tons });
    else if (orders.get(key).tons === null) orders.get(key).tons = tons;
  };
  if (kind === "loading") {
    const mill = millKey(primary.calculation_details?.mill);
    for (const order of visit?.orders || []) {
      // A collective loading charge covers the entire visit. Physical and billing identities may differ.
      if (sharedVisit && ![order.source_mill, order.mill].some((source) => millKey(source) === mill)) continue;
      add(order.id, order.label, billingNumber(order[field]));
    }
  }
  for (const item of members) {
    const details = item.calculation_details || {};
    if (details.order_id == null && !item.order_reference) continue;
    const order = visit?.orders?.find((entry) => details.order_id != null
      ? String(entry.id) === String(details.order_id) : entry.label === item.order_reference);
    // Delivery numerators are saved delivered tonnages, not attributed durations.
    const tons = (details.kind === "delivery" ? billingNumber(details.coefficient?.numerator) : null)
      ?? billingNumber(order?.[field]);
    add(details.order_id ?? order?.id, item.order_reference || order?.label, tons);
  }
  return [...orders.values()];
}

// Presentation only: retain the original charges for totals, rates and allocation details.
export function consolidationStopGroups(items, { visits = [], labels = new Map(), inspectionLabel = "", charges = [] } = {}) {
  const visitsById = new Map(visits.map((visit) => [String(visit.id), visit]));
  return groupBillableCalculationSteps(items).map((group) => {
    const stops = new Map();
    for (const item of group.items) {
      const details = item.calculation_details || {};
      const visitId = details.visit_id || details.block?.visit_id;
      const key = visitId ? `visit:${visitId}` : `charge:${item.id}`;
      if (!stops.has(key)) stops.set(key, { key, visit: visitsById.get(String(visitId)), items: [] });
      stops.get(key).items.push(item);
    }
    const result = [...stops.values()].map(({ key, visit, items: members }) => {
      const hourly = members.filter((item) => item.billing_unit === "heure");
      const primary = hourly.find((item) => ["loading", "delivery"].includes(item.calculation_details?.kind)) || members[0];
      const kind = primary.calculation_details?.kind;
      const shared = ["inspection", "return"].includes(kind);
      const reference = kind === "inspection" ? inspectionLabel : visit ? visitDispatchLabel(visit, labels) : "—";
      const quantity = hourly.length ? hourly.reduce((total, item) => {
        const value = billingNumber(item.quantity);
        return total === null || value === null ? null : total + value;
      }, 0) : null;
      const amounts = members.map((item) => billingNumber(item.line_total));
      const visitId = primary.calculation_details?.visit_id || primary.calculation_details?.block?.visit_id;
      const visitCharges = charges.filter((charge) => visitId && String(charge.visit_id || charge.block?.visit_id) === String(visitId)
        && charge.revision_id === primary.calculation_details?.revision_id);
      // Use identities from the same saved calculation, without mixing physical and billing IDs.
      const mills = new Set((visitCharges.length ? visitCharges.map((charge) => millKey(charge.mill))
        : (visit?.orders || []).map((order) => millKey(order.source_mill || order.mill))).filter(Boolean));
      const sharedVisit = mills.size > 1;
      const orders = stopOrders(members, visit, primary, sharedVisit);
      const totalTons = orders.length && orders.every((order) => order.tons !== null)
        ? orders.reduce((total, order) => total + order.tons, 0) : null;
      // Commands repeat the physical block. Read one copy; sum only attributed quantities above.
      const block = hourly.find((item) => item.calculation_details?.block)?.calculation_details.block || null;
      const inspectionQuantity = hourly.reduce((total, item) => total + (billingNumber(item.inspection_quantity) || 0), 0);
      return {
        ...primary, id: `${group.key}:${key}`, quantity: hourly.length ? quantity : primary.quantity,
        line_total: amounts.every((amount) => amount !== null) ? amounts.reduce((cents, amount) => cents + Math.round(amount * 100), 0) / 100 : null,
        stop: {
          reference: kind === "return" || kind === "preload" ? "" : reference === "—" ? "" : reference,
          missingReference: !shared && kind !== "preload" && reference === "—",
          label: shared || kind === "preload" ? billableCalculationLabel(primary) : visit?.label || "Lieu non disponible",
          operation: kind === "loading" ? "Chargement" : kind === "delivery" ? "Livraison" : billableCalculationLabel(primary),
          shared, sharedVisit, orders, totalTons, block,
          items: members, hasDuration: hourly.length > 0,
          ...(inspectionQuantity > 0 ? { inspectionQuantity } : {}),
        },
      };
    });
    // A supplement without a block must not move its visit ahead of earlier stops.
    // Sort again using each stop's hourly charge and its saved actual block time.
    return { ...group, items: groupBillableCalculationSteps(result).flatMap((part) => part.items) };
  });
}
